import type { VerdictValue } from '@threatlens/design-system';

/**
 * One canonical verdict vocabulary for the SPA.
 *
 * The wire uses three spellings for the same idea: `Verdict.verdict` is `clean`, `SampleUrlRow`
 * and the URL-scan rows say `benign`, and the community vote/consensus fields say `harmless`.
 * `VerdictValue` - the design system's `VerdictBadge` contract - only knows `clean`.
 *
 * Several call sites bridged that gap with `as VerdictValue` on a field declared `string | null`,
 * which type-checks and then renders NOTHING: `STYLES[undefined]` and `LABELS[undefined]` are both
 * undefined, so the badge paints as an unstyled pill with no text. Route every wire verdict through
 * `asVerdict` instead, and never cast.
 */
const ALIASES: Record<string, VerdictValue> = {
  // Alternate spellings of "we looked and found nothing bad".
  benign: 'clean',
  harmless: 'clean',
  // Alternate spellings of the canonical values themselves.
  malicious: 'malicious',
  suspicious: 'suspicious',
  clean: 'clean',
  unknown: 'unknown',
  insufficient: 'insufficient',
  // Some engine rows say "undetected", which is an observation, not a clean verdict. Treat it as
  // insufficient evidence rather than asserting the sample is clean.
  undetected: 'insufficient',
};

/**
 * Coerces any wire verdict to a `VerdictValue`. Anything unrecognised becomes `unknown` - never
 * `clean`, because an absent or unreadable verdict must not read as a clean bill of health.
 */
export function asVerdict(value: unknown): VerdictValue {
  if (typeof value !== 'string') return 'unknown';
  return ALIASES[value.trim().toLowerCase()] ?? 'unknown';
}

/**
 * Like `asVerdict` but preserves "we have no verdict at all", for call sites that render nothing
 * rather than an `unknown` chip.
 */
export function asVerdictOrNull(value: unknown): VerdictValue | null {
  if (value === null || value === undefined || value === '') return null;
  return asVerdict(value);
}
